import { useSyncExternalStore } from "react";

import { readDevice, writeDevice } from "./device-storage";

export const THEMES = ["light", "dark", "system"] as const;
export type Theme = (typeof THEMES)[number];

const listeners = new Set<() => void>();
let current: Theme = parseTheme(readDevice("theme"));

function parseTheme(value: string | undefined): Theme {
  return THEMES.find((theme) => theme === value) ?? "system";
}

function prefersDark(): boolean {
  return (
    typeof globalThis.matchMedia === "function" &&
    globalThis.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Puts `.dark` on <html> for the chosen theme (or the system's, for `system`). */
export function applyTheme(theme: Theme = current): void {
  const dark = theme === "dark" || (theme === "system" && prefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

export function setTheme(theme: Theme): void {
  current = theme;
  writeDevice("theme", theme);
  applyTheme(theme);
  for (const listener of listeners) listener();
}

/** Follows the system setting while the theme is `system`. Call once at startup. */
export function watchSystemTheme(): void {
  if (typeof globalThis.matchMedia !== "function") return;
  globalThis.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (current === "system") applyTheme();
  });
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}
